import { ProtectedComponent } from '@/domain/components/rbac/ProtectedComponent';
import { usePermission } from '@/domain/context/rbac';
import { columnsReports } from '@/domain/env/colums-reports';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Report } from '@/domain/types/collections';
import usePaginatedDocuments from '@/domain/use-cases/get-paginated-documents';
import Button from '@/presentation/components/button';
import LoadMoreButton from '@/presentation/components/load-more-button';
import Table from '@/presentation/components/table';
import { useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';

const ReportsPages = () => {
  const navigate = useNavigate();
  const canCreate = usePermission('reports', 'create');
  const { data, error, isLoading, isLoadingMore, hasMore, loadMore } =
    usePaginatedDocuments<Report>(DOCUMENTS_DB.REPORTS);

  const [searchTitle, setSearchTitle] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [searchDate, setSearchDate] = useState('');

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!data.length && !isLoading) return <div>No hay datos</div>;

  // Filtrar por título, categoría y fecha
  const filteredData = data.filter((item) => {
    const matchesTitle = item.title
      .toLowerCase()
      .includes(searchTitle.toLowerCase());
    const matchesCategory = searchCategory
      ? item.category === searchCategory
      : true;
    const matchesDate = searchDate
      ? item.createdAt &&
        item.createdAt.seconds &&
        new Date(item.createdAt.seconds * 1000).toISOString().slice(0, 10) ===
          searchDate
      : true;
    return matchesTitle && matchesCategory && matchesDate;
  });

  const handleRowClick = (row: Report) => {
    navigate(`/dashboard/reports/details/${row.id}`);
  };

  return (
    <ProtectedComponent
      resource="reports"
      action="read"
      fallback={
        <div className="flex justify-center items-center min-h-96">
          <div className="text-center">
            <h2 className="text-lg font-medium text-gray-900">
              Acceso Denegado
            </h2>
            <p className="text-gray-600 mt-1">
              No tienes permisos para ver esta página
            </p>
          </div>
        </div>
      }
    >
      <div className="p-4 flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Reportes</h1>
          {canCreate && (
            <Link to="/dashboard/reports/add">
              <Button
                label="Agregar reporte"
                icon={<FiPlus />}
                onClick={() => {}}
              />
            </Link>
          )}
        </div>

        {/* Filtros de búsqueda */}
        <div className="flex flex-wrap gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Título</label>
            <input
              type="text"
              placeholder="Buscar por título"
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              className="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[200px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Categoría
            </label>
            <select
              value={searchCategory}
              onChange={(e) => setSearchCategory(e.target.value)}
              className="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[260px] bg-white"
            >
              <option value="">Todas las categorías</option>
              <option value="blasting">Cronograma de Voladura</option>
              <option value="meeting">
                Reporte de Seguridad de Reunión Diaria de Mina
              </option>
              <option value="brigades">
                Reporte de Entrenamiento de Brigadas
              </option>
              <option value="safety">
                Reportes de Entrenamiento de Seguridad
              </option>
              <option value="incidents">
                Sistema de Comando de Incidentes de Turno
              </option>
              <option value="maintenance">Reporte de Mantenimiento</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Fecha de creación
            </label>
            <input
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              className="px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <Table
          data={filteredData}
          columns={columnsReports}
          onRowClick={handleRowClick}
        />

        <LoadMoreButton
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMore}
        />
      </div>
    </ProtectedComponent>
  );
};

export default ReportsPages;
